/**
 * Which nodes can be annotated, and through which output port.
 *
 * Derived from the generated `YDOC_NODE_FIELDS` rather than a hand-written list
 * of node types, so adding an image-producing node to `schemas/nodes/` makes it
 * annotatable with no change here (CLAUDE.md: "no hand-written mappings").
 *
 * The rule mirrors the backend's `_single_image_output_port`
 * (`agentic_workflow/agents/director/director_tools.py`), which is what the
 * Director's `annotate_image` uses to pick a source port — so the two surfaces
 * agree on which nodes are annotatable by construction rather than by
 * coincidence.
 *
 * SCHEMA ELIGIBILITY IS NECESSARY BUT NOT SUFFICIENT. Nine backend types pass
 * this test; five carry the Annotate action. The other four are excluded for
 * concrete reasons, not oversight — the editor annotates *what the user can
 * see*, and these nodes show no image:
 *
 *   panel_source, webtoon_panel_generator
 *     No entry in `nodeRegistry` at all. They never render on the canvas, so
 *     there is no card to attach an entry point to.
 *
 *   storyboard_designer, storyboard_no_enhance_designer
 *     Rendered by `StoryboardDesignerNode`, whose card is a `FlatIdleNodeCard`
 *     plus beat text and whose bottom panel is Config-only. Its `out_image`
 *     (the storyboard grid) is never displayed on the node, so an editor there
 *     would open an overlay onto a picture the node does not show.
 *
 * The Director's `annotate_image` is unaffected: it reads the asset directly and
 * needs no rendered card, which is why the backend rule is broader on purpose.
 * If one of these nodes ever grows an image preview, wiring it is the same two
 * lines as everywhere else — this function already returns its port.
 */

import { FE_TYPE_TO_BACKEND, YDOC_NODE_FIELDS } from '../../../../../generated/nodeDataTypes'

/**
 * The single, non-array image output port of a node type, or null.
 *
 * "Single" is load-bearing: a node with several image outputs, or an array one,
 * has no unambiguous source for the provenance edge, so it is not annotatable
 * through this entry point.
 */
export function singleImageOutputPort(feNodeType: string): string | null {
  const backendType = FE_TYPE_TO_BACKEND[feNodeType] ?? feNodeType
  const fields = YDOC_NODE_FIELDS[backendType]
  if (!fields) return null

  let found: string | null = null
  for (const [fieldId, spec] of Object.entries(fields)) {
    if (!fieldId.startsWith('out_')) continue
    if (spec.baseType !== 'ImageAsset') continue
    if (spec.wireType !== 'map') continue
    if (spec.isMulti) continue
    // A second match means the source is ambiguous — refuse rather than guess.
    if (found !== null) return null
    found = fieldId
  }
  return found
}

/** True when this node type exposes exactly one single image output. */
export function isAnnotatableNodeType(feNodeType: string): boolean {
  return singleImageOutputPort(feNodeType) !== null
}
